import React, { useState, useRef, useEffect } from "react";

const RPE_VALUES = [6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10];
const DEC_OPTIONS = [0, 25, 50, 75];

// Palette calquée sur l'app mobile (screens/training/Exercice.js:getRpeColor)
// Gradient d'effort ressenti : modéré → max
const RPE_COLOR_ORANGE = "#FF9800";
const RPE_COLOR_RED = "#F44336";
const RPE_COLOR_DEEP = "#B71C1C";

function getRpeColor(v) {
	if (!v || v <= 0) return null;
	if (v <= 7) return RPE_COLOR_ORANGE;
	if (v <= 8.5) return RPE_COLOR_RED;
	return RPE_COLOR_DEEP;
}

function formatRpe(v) {
	if (v == null) return "";
	if (Number.isInteger(v)) return String(v);
	return v.toFixed(1);
}

function formatDec(d) {
	// 0 -> ".00", 25 -> ".25" …
	return `.${String(d).padStart(2, "0")}`;
}

function RowMenu({ open, onClose, onDuplicate, onRemove, canRemove }) {
	const ref = useRef(null);
	useEffect(() => {
		if (!open) return;
		const onClick = (e) => {
			if (ref.current && !ref.current.contains(e.target)) onClose();
		};
		document.addEventListener("mousedown", onClick);
		return () => document.removeEventListener("mousedown", onClick);
	}, [open, onClose]);

	if (!open) return null;
	return (
		<div ref={ref} className="editor-series-row-menu" role="menu">
			<button className="editor-series-row-menu-item" onClick={() => { onDuplicate?.(); onClose(); }}>
				Dupliquer
			</button>
			<button className="editor-series-row-menu-item danger" onClick={() => { onRemove?.(); onClose(); }} disabled={!canRemove}>
				Supprimer
			</button>
		</div>
	);
}

function RpeModal({ open, value, onPick, onClose }) {
	useEffect(() => {
		if (!open) return;
		const onKey = (e) => { if (e.key === "Escape") onClose(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	if (!open) return null;
	const current = parseFloat(value || 0);
	return (
		<div className="rpe-modal-overlay" onClick={onClose}>
			<div className="rpe-modal" onClick={(e) => e.stopPropagation()}>
				<div className="rpe-modal-header">
					<span className="rpe-modal-title">RPE</span>
					<button className="rpe-modal-close" onClick={onClose} aria-label="Fermer">×</button>
				</div>
				<div className="rpe-modal-grid">
					{RPE_VALUES.map((v) => {
						const color = getRpeColor(v);
						const active = current === v;
						return (
							<button
								key={v}
								className={`rpe-modal-val ${active ? "active" : ""}`}
								onClick={() => { onPick(active ? 0 : v); onClose(); }}
								style={{
									background: active ? color : color + "20",
									borderColor: active ? color : color + "55",
									color: active ? "#fff" : color,
									boxShadow: active ? `0 2px 10px ${color}66` : "none",
								}}
							>
								{formatRpe(v)}
							</button>
						);
					})}
				</div>
				<button className="rpe-modal-clear" onClick={() => { onPick(0); onClose(); }}>
					Aucun RPE
				</button>
			</div>
		</div>
	);
}

export default function SeriesTable({
	series,
	onUpdateReps,
	onUpdatePoids,
	onUpdatePoidsDec,
	onUpdateRpe,
	onAddSerie,
	onRemoveSerie,
	onDuplicateSerie,
}) {
	const [openMenu, setOpenMenu] = useState(null);
	const [rpeEditing, setRpeEditing] = useState(null); // serieIndex being edited

	return (
		<div className="editor-series-table">
			<div className="editor-series-table-header">
				<span className="col-num">SÉRIE</span>
				<span className="col-reps">RÉPS</span>
				<span className="col-charge">CHARGE</span>
				<span className="col-rpe">RPE</span>
				<span className="col-menu" aria-hidden="true" />
			</div>

			{series.map((serie, serieIndex) => {
				const rpe = parseFloat(serie.rpe || 0);
				const dec = serie.poidsDec || 0;
				return (
					<div key={serie.id != null ? `${serie.id}-${serieIndex}` : serieIndex} className="editor-series-row">
						<span className="col-num">
							#{serieIndex + 1}
						</span>

						<div className="col-reps editor-series-input-wrap">
							<input
								type="number"
								className="editor-series-input"
								value={serie.reps ?? 0}
								onChange={(e) => onUpdateReps?.(serieIndex, e.target.value)}
							/>
							<span className="editor-series-input-unit">rep</span>
						</div>

						<div className="col-charge editor-series-charge">
							<input
								type="number"
								step="1"
								className="editor-series-input editor-series-charge-int"
								value={serie.poids ?? 0}
								onChange={(e) => onUpdatePoids?.(serieIndex, e.target.value)}
							/>
							<select
								className="editor-series-charge-dec"
								value={dec}
								onChange={(e) => onUpdatePoidsDec?.(serieIndex, e.target.value)}
								title="Décimale (kg)"
							>
								{DEC_OPTIONS.map((d) => (
									<option key={d} value={d}>{formatDec(d)}</option>
								))}
							</select>
							<span className="editor-series-input-unit">kg</span>
						</div>

						<div className="col-rpe">
							<button
								type="button"
								className={`editor-series-rpe-pill ${rpe > 0 ? "set" : ""}`}
								onClick={() => setRpeEditing(serieIndex)}
								title="Définir le RPE"
								style={
									rpe > 0
										? {
												background: getRpeColor(rpe) + "20",
												borderColor: getRpeColor(rpe) + "55",
												color: getRpeColor(rpe),
											}
										: undefined
								}
							>
								{rpe > 0 ? formatRpe(rpe) : ""}
							</button>
						</div>

						<div className="col-menu">
							<button
								type="button"
								className="editor-series-row-menu-btn"
								onClick={() => setOpenMenu(openMenu === serieIndex ? null : serieIndex)}
								title="Plus"
								aria-label="Options de la série"
							>
								<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
									<circle cx="5" cy="12" r="1.6" />
									<circle cx="12" cy="12" r="1.6" />
									<circle cx="19" cy="12" r="1.6" />
								</svg>
							</button>
							<RowMenu
								open={openMenu === serieIndex}
								onClose={() => setOpenMenu(null)}
								onDuplicate={() => onDuplicateSerie?.(serieIndex)}
								onRemove={() => onRemoveSerie?.(serieIndex)}
								canRemove={series.length > 1}
							/>
						</div>
					</div>
				);
			})}

			<button type="button" className="editor-series-add-row" onClick={onAddSerie}>
				+ Ajouter une série
			</button>

			<RpeModal
				open={rpeEditing != null}
				value={rpeEditing != null ? series[rpeEditing]?.rpe : 0}
				onPick={(v) => onUpdateRpe?.(rpeEditing, v)}
				onClose={() => setRpeEditing(null)}
			/>
		</div>
	);
}
